import React, { Fragment, useContext, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Observer } from 'mobx-react-lite';
import FadeIn from 'react-fade-in';
import { toast } from 'react-toastify';
import momentLocalizer from 'react-widgets-moment';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModal';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import RichTextEditor from '../../_shared/RichTextEditor';

import AppointmentUpdateStore from '../../../../stores/AppointmentUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import * as rts from '../../../../constants/routes';
import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as ah from '../../../../utilities/addressHelper';
import * as ch from '../../../../utilities/customerHelper';
import * as aph from '../../../../utilities/appointmentHelper';
import * as bh from '../../../../utilities/badgeHelper';
import * as ph from '../../../../utilities/personHelper';
import * as sys from '../../../../utilities/systemHelper';
import * as oh from '../../../../utilities/operationHelper';

import './DeleteAppointment.scss';

moment.locale('en');
momentLocalizer();

function DeleteAppointment(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const appointment = useContext(AppointmentUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [cancelType, setCancelType] = useState(null);

    useEffect(() => {
        quickDrawerFocus(props.drawer);

        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (appointment.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleRequestByChange = event => {
        appointment.cancelRequestBy = event.target.value;
        appointment.hasUnsavedChanges = true;
    }

    const handleReasonChange = (content, delta, source, editor) => {
        const reason = editor.getText().replace(/(\r\n|\n|\r)/g, ' ').trim();
        const reasonHtml = (content === '<p><br></p>') ? null : content;

        appointment.cancelRequestReason = reason;
        appointment.cancelRequestReasonHtml = reasonHtml;
        appointment.hasUnsavedChanges = true;
    }

    const handleRepeatUpdateFollowingChange = event => {
        const value = event.target.value;
        setCancelType(value);
        appointment.hasUnsavedChanges = true;
    }

    const handleSubmit = event => {
        event.preventDefault();
        if (isMounted.current) {
            if (fn.validateForm(validateRef.current)) {
                setConfirmDelete(true);
            }
        }
    }

    const handleConfirmDelete = event => {
        event.preventDefault();

        if (!!cancelType) {
            switch (cancelType) {
                case 'ThisAppointmentOnly':
                    appointment.repeatUpdateFollowing = false;
                    break;

                case 'ThisAppointmentExtend':
                    appointment.repeatUpdateFollowing = false;
                    appointment.deleteAndExtend = true;
                    break;

                case 'OutstandingAppointments':
                    appointment.repeatUpdateFollowing = true;
                    break;

                default:
                    break;
            }
        }

        appointment.delete()
            .then(data => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: true, data: data });
                    }
                }
            })
            .catch(error => {
                const msg = <p><strong>Cannot cancel appointment.</strong>{error && error.response && error.response.data ? <>&nbsp;{error.response.data}</> : null}</p>;
                toast.error(() => msg, { position: 'top-center', style: { width: '380px' } });
            })

        setConfirmDelete(false);
    }

    const handleConfirmCancel = event => {
        event.preventDefault();
        setConfirmDelete(false);

        if (props.onCancel && fn.isFunction(props.onCancel)) {
            props.onCancel(event);
        }
    }

    const renderAppointmentDateTime = () => {
        if (!appointment.data.start) return null;

        const start = moment(appointment.data.start);
        const end = appointment.data.end ? moment(appointment.data.end) : null;
        const hasEndTime = !!end;
        const weekday = start.format('dddd');
        const day = start.format('D');
        const ordinal = start.format('Do').replace(day, '');
        const dateHtml = `${start.format('MMMM D')}<sup>${ordinal}</sup>${((start.year() !== moment().year()) ? `, ${start.format('YYYY')}` : '')}`;
        const startTimeHtml = `${start.format('h:mm')}${(!hasEndTime || start.format('a') !== end.format('a') ? ` ${start.format('a')}` : '')}`;
        const endTimeHtml = hasEndTime ? `${end.format('h:mm')} ${end.format('a')}` : '';

        return <ul className='list-inline no-style m-0'>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <small className='weekday'>{weekday}</small>
                    <span className='date' dangerouslySetInnerHTML={{ __html: dateHtml }}></span>
                </div>
            </li>
            <li className='list-inline-item my-0 mx-1'><small>@</small></li>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <span className='time' dangerouslySetInnerHTML={{ __html: startTimeHtml }}></span>
                </div>
            </li>
            <li className='list-inline-item my-0 mx-1'><small>to</small></li>
            <li className='list-inline-item m-0'>
                <div className='text'>
                    <span className='time' dangerouslySetInnerHTML={{ __html: endTimeHtml }}></span>
                </div>
            </li>
        </ul>
    }

    const renderResource = () => {
        const { resource } = appointment.data;

        return <div
            className='profile-wrapper'
        >
            <div className='profile'>
                <span
                    className={`profile-image rounded-circle d-block fw-500` + (resource && !resource.profilePictureUri ? ` profile-initials bg-color${resource.color}-500` : '')}
                    style={resource && resource.profilePictureUri ? {
                        backgroundImage: `url(${resource.profilePictureUri})`,
                        backgroundSize: 'cover',
                    } : null}
                    title={resource ? resource.fullName : 'System'}>
                    {resource && !resource.profilePictureUri ? <div className='d-initials'>{resource.initials}</div> : null}
                </span>
            </div>
            <div className='description'>
                <div className='name'>{resource.fullName}</div>
                <div className='services'>
                    {bh.renderAppointmentNew(appointment.data, 'mr-1')}
                    {bh.renderAppointmentFirstExam(appointment.data, 'mr-1')}
                    {bh.renderAppointmentPrebook(appointment.data, 'mr-1')}
                    {
                        appointment.data.services.map((s, si) => {
                            return <Fragment
                                key={`service_${si}`}
                            >
                                {bh.renderServiceCode(s, 'p-1 mr-1', (!s.isEligible || !aph.isEndState(appointment.data)))}
                            </Fragment>
                        })
                    }
                    {bh.renderAppointmentRescheduledByOffice(appointment.data, 'mr-1')}
                    {bh.renderAppointmentRescheduledByCustomer(appointment.data, 'mr-1')}
                </div>
            </div>
        </div>
    }

    const renderCustomer = () => {
        const customer = appointment.data.customer;
        return <div
            className='profile-wrapper'
        >
            <div className='profile'>
                <span
                    className={`profile-image rounded-circle d-flex profile-initials ${ch.getProfileBgColor(customer)} fw-500`}
                    title={customer.fullName}
                >
                    {customer.initials}
                </span>
            </div>
            <div className='description'>
                <Link
                    to={`${rts.Customers.Home}/${customer.id}`}
                    className='name text-gray-700 text-info-hover'
                    onClick={quickDrawer.deactivateAll}>{ph.getFullName(customer, true)}
                    {
                        customer.dateOfBirth || customer.sex || customer.gender || customer.pronoun ?
                            <small className='text-nowrap ml-2'>({`${ph.getAge(customer.dateOfBirth, moment(appointment.data.start))} ${ph.getSexGenderPronounDisplay(customer)}`.trim()})</small> : null
                    }
                </Link>
                {
                    customer.address && customer.address.country ?
                        <div className='info'>{ah.getAddressHtml(customer.address)}</div> : null
                }
                {
                    customer.emailAddress ?
                        <div className='info'>
                            <a
                                href={`mailto:${customer.emailAddress}`}
                            >{customer.emailAddress}
                            </a>
                        </div> : null
                }
                {
                    customer.phoneNumber ?
                        <div className='info'>
                            <a
                                href={`tel:${customer.phoneNumber}`}
                            >{sys.getFormattedPhoneNumber(customer.phoneNumber)}
                            </a>
                        </div> : null
                }
                {
                    customer.primaryContactPerson ?
                        <div className='mt-2 border-left border-3 border-gray-300 pl-3 py-1'>
                            <small className='text-primary-400 fs-75 text-uppercase d-block'>Primary Contact</small>
                            {
                                customer.primaryContactPerson.id ?
                                    <Link
                                        to={`${rts.Customers.Home}/${customer.primaryContactPerson.id}`}
                                        className='d-block fs-lg text-gray-700 text-info-hover'
                                        onClick={quickDrawer.deactivateAll}
                                    >
                                        {ph.getPreferredFirstLastName(customer.primaryContactPerson)}
                                        {
                                            customer.primaryContactPerson.relationship ?
                                                <small className='ml-1'>(<span className='fw-500 fs-90 text-success-700'>{customer.primaryContactPerson.relationship}</span>)</small> : null
                                        }
                                    </Link> :
                                    <div className='fs-lg text-gray-700 '>{ph.getPreferredFirstLastName(customer.primaryContactPerson)}
                                        {
                                            customer.primaryContactPerson.relationship ?
                                                <small className='ml-1'>(<span className='fw-500 fs-90 text-success-700'>{customer.primaryContactPerson.relationship}</span>)</small> : null
                                        }
                                    </div>
                            }
                            {
                                customer.primaryContactPerson.emailAddress ?
                                    <div className='info fs-95'>
                                        <a
                                            href={`mailto:${customer.primaryContactPerson.emailAddress}`}
                                        >{customer.primaryContactPerson.emailAddress}
                                        </a>
                                    </div> : null
                            }
                            {
                                customer.primaryContactPerson.phoneNumber ?
                                    <div className='info fs-95'>
                                        <a
                                            href={`tel:${customer.primaryContactPerson.phoneNumber}`}
                                        >{sys.getFormattedPhoneNumber(customer.primaryContactPerson.phoneNumber)}
                                        </a>
                                    </div> : null
                            }
                        </div> : null
                }
            </div>
        </div>
    }

    return <>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <>
                    {
                        (props.drawer === quickDrawer.drawerOpened) && !confirmDelete ?
                            <GlobalHotKeys
                                keyMap={{
                                    close: ['esc'],
                                }}
                                handlers={{
                                    close: event => {
                                        handleCancel(event)
                                    },
                                }}
                                allowChanges={true}
                            /> : null
                    }
                    <fieldset disabled={appointment.isSaving}>
                        <div className='quick-drawer'>
                            <QuickDrawerHeader
                                drawer={props.drawer}
                                icon={oh.getIcon('appointment', 'delete')}
                                action='Delete'
                                category='Appointment'
                                className='delete'
                                onCancel={handleCancel}
                            />
                            <div className='quick-drawer-body'>
                                {
                                    appointment.isReady ?
                                        <FadeIn>
                                            <div className='delete-appointment body-content'>
                                                <section className='date-time'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            {renderAppointmentDateTime()}
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='resource'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-0'>
                                                                <label><small>With</small></label>
                                                                {renderResource()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='customer'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-0'>
                                                                <label><small>For</small></label>
                                                                {renderCustomer()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <Observer>{() =>
                                                                <div className={'form-group validate validate-required mb-0'}>
                                                                    <label className='required'><small>Cancel by</small></label>
                                                                    <div className='custom-control custom-radio mb-2'>
                                                                        <input
                                                                            id='requestByCustomer'
                                                                            type='radio'
                                                                            name='requestBy'
                                                                            value='Customer'
                                                                            className='custom-control-input'
                                                                            checked={appointment.cancelRequestBy === 'Customer'}
                                                                            onChange={handleRequestByChange}
                                                                        />
                                                                        <label
                                                                            htmlFor='requestByCustomer'
                                                                            className='custom-control-label'
                                                                        >
                                                                            {
                                                                                appointment.originalData ?
                                                                                    <>{ph.getFullName(appointment.originalData.customer, true)} <span className='text-smaller text-gray-700'>(Patient)</span></> : <>Patient</>
                                                                            }
                                                                        </label>
                                                                    </div>
                                                                    <div className='custom-control custom-radio mb-2'>
                                                                        <input
                                                                            id='requestByResource'
                                                                            type='radio'
                                                                            name='requestBy'
                                                                            value='Resource'
                                                                            className='custom-control-input'
                                                                            checked={appointment.cancelRequestBy === 'Resource'}
                                                                            onChange={handleRequestByChange}
                                                                        />
                                                                        <label
                                                                            htmlFor='requestByResource'
                                                                            className='custom-control-label'
                                                                        >
                                                                            {
                                                                                appointment.originalData ?
                                                                                    <>{ph.getFullName(appointment.originalData.resource, true)} <span className='text-smaller text-gray-700'>(Our office)</span></> : <>Doctor</>
                                                                            }
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            }</Observer>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className={'form-group'}>
                                                                <label><small>Cancel Reason</small></label>
                                                                <div className='input-group'>
                                                                    <Observer>{() =>
                                                                        <RichTextEditor
                                                                            mode='basic'
                                                                            value={appointment.cancelRequestReasonHtml}
                                                                            disableTab={true}
                                                                            onChange={handleReasonChange}
                                                                        />
                                                                    }</Observer>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>

                                                <Observer>{() =>
                                                    <>
                                                        {
                                                            appointment.data.isRepeating && appointment.data.repeatIndex !== appointment.data.repeatTotal ?
                                                                <>
                                                                    <section>
                                                                        <div className='row'>
                                                                            <div className='col-12'>
                                                                                <div className='form-group'>
                                                                                    <label className='required'><small>Canceling</small></label>
                                                                                    <Observer>{() =>
                                                                                        <>
                                                                                            <div className='custom-control custom-radio mb-2'>
                                                                                                <input
                                                                                                    id='deleteThisAppointment'
                                                                                                    type='radio'
                                                                                                    name='repeatDeleteFollowing'
                                                                                                    value='ThisAppointmentOnly'
                                                                                                    className='custom-control-input'
                                                                                                    checked={cancelType === 'ThisAppointmentOnly'}
                                                                                                    onChange={handleRepeatUpdateFollowingChange}
                                                                                                />
                                                                                                <label
                                                                                                    htmlFor='deleteThisAppointment'
                                                                                                    className='custom-control-label'
                                                                                                >
                                                                                                    This appointment only
                                                                                                </label>
                                                                                            </div>
                                                                                            <div className='custom-control custom-radio mb-2'>
                                                                                                <input
                                                                                                    id='deleteThisAppointmentExtend'
                                                                                                    type='radio'
                                                                                                    name='repeatDeleteFollowing'
                                                                                                    value='ThisAppointmentExtend'
                                                                                                    className='custom-control-input'
                                                                                                    checked={cancelType === 'ThisAppointmentExtend'}
                                                                                                    onChange={handleRepeatUpdateFollowingChange}
                                                                                                />
                                                                                                <label
                                                                                                    htmlFor='deleteThisAppointmentExtend'
                                                                                                    className='custom-control-label'
                                                                                                >
                                                                                                    This appointment but extend end date
                                                                                                </label>
                                                                                            </div>
                                                                                            <div className='custom-control custom-radio mb-2'>
                                                                                                <input
                                                                                                    id='deleteOutstandingAppointments'
                                                                                                    type='radio'
                                                                                                    name='repeatDeleteFollowing'
                                                                                                    value='OutstandingAppointments'
                                                                                                    className='custom-control-input'
                                                                                                    checked={cancelType === 'OutstandingAppointments'}
                                                                                                    onChange={handleRepeatUpdateFollowingChange}
                                                                                                />
                                                                                                <label
                                                                                                    htmlFor='deleteOutstandingAppointments'
                                                                                                    className='custom-control-label'
                                                                                                >
                                                                                                    This and following appointment(s)
                                                                                                </label>
                                                                                            </div>
                                                                                        </>
                                                                                    }</Observer>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </section>
                                                                </> : null
                                                        }
                                                    </>
                                                }</Observer>
                                            </div>
                                        </FadeIn> : renderQuickDrawerLoading()
                                }
                            </div>
                            <Observer>{() =>
                                appointment.isReady ?
                                    <div className='quick-drawer-action'>
                                        <div className='row'>
                                            <div className='col-12'>
                                                <div className='float-right'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-link btn-cancel mr-2'
                                                        onClick={props.onCancel}
                                                    >Cancel</button>
                                                    <Observer>{() =>
                                                        <button
                                                            data-delete-appointment
                                                            type='submit'
                                                            className='btn btn-danger'
                                                        >Delete</button>
                                                    }</Observer>
                                                </div>
                                            </div>
                                        </div>
                                    </div> : null
                            }</Observer>
                        </div>
                    </fieldset>
                </>
            }</Observer>
        </form>
        <BodyEnd>
            <ConfirmModal
                icon={<i className='fal fa-calendar-times text-danger mr-2'></i>}
                message={<>Continue to <span data-continue-delete-appointment className='fw-500'>&nbsp;delete {appointment.originalData ? ph.getPreferredFirstName(appointment.originalData.customer) : ''}'s appointment</span>? </>}
                show={confirmDelete}
                onOption1Click={handleConfirmDelete}
                onCancel={handleConfirmCancel}
            />
        </BodyEnd>
    </>
}

export default DeleteAppointment;